<template>
  <div class="msg-content">
    <!-- 摘要模式：表格单元格 / descriptions 中的预览，去标记后最多两行，超出省略 -->
    <span v-if="inline" class="msg-inline" :title="plainText">{{ plainText || '—' }}</span>
    <!-- 空内容 -->
    <span v-else-if="!normalized" class="msg-empty">—</span>
    <!-- 纯文本：保留原始换行与空格 -->
    <pre v-else-if="contentType === 'text'" class="msg-text" :style="bodyStyle">{{ normalized }}</pre>
    <!-- markdown / html：渲染后输出 -->
    <div v-else class="msg-body" :class="contentType === 'markdown' ? 'markdown-body' : 'msg-html'" :style="bodyStyle" v-html="rendered" />
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it'
// 强制浅色主题：github-markdown.css 会跟随系统深色模式，嵌入后台会白字白底
import 'github-markdown-css/github-markdown-light.css'

// html:false —— markdown 中的原始 HTML 会被转义，避免直接执行脚本
const md = new MarkdownIt({ html: false, linkify: true, breaks: true })

// 常见 HTML 标签：命中即按 html 渲染（模板中的 html 类型、富文本内容）
const HTML_TAG_RE =
  /<\/?(?:p|br|div|span|h[1-6]|ul|ol|li|table|thead|tbody|tr|td|th|strong|b|em|i|u|a|img|blockquote|pre|code|hr|font|section|article|figure|video|audio|iframe)\b[^>]*>/i
// 常见 markdown 语法：标题 / 列表 / 引用 / 代码块 / 表格 / 链接 / 强调
const MARKDOWN_RE = /(^|\n)\s{0,3}(#{1,6}\s|[-*+]\s|\d+\.\s|>\s|```|\|.+\|)|!?\[[^\]]*\]\([^)]+\)|\*\*[^\s*]/

/** 非字符串内容（如通知历史 result 对象）统一转成缩进 JSON，便于阅读 */
function normalize(content) {
  if (content === null || content === undefined) return ''
  if (typeof content === 'string') return content
  try {
    return JSON.stringify(content, null, 2)
  } catch (e) {
    return String(content)
  }
}

/**
 * 内容本身是 JSON 字符串时缩进美化
 * 场景：事件消息里存的是 {"title":"..","markdown":".."} 这类结构化载荷，原样输出是一行转义文本，无法阅读
 * @returns {string} 美化结果；不是 JSON 时返回空串
 */
function tryPrettyJson(text) {
  const trimmed = text.trim()
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return ''
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2)
  } catch (e) {
    return ''
  }
}

/**
 * 识别内容类型
 * @param {*} content 原始内容
 * @returns {'text'|'markdown'|'html'}
 */
export function detectContentType(content) {
  const text = normalize(content)
  if (!text.trim()) return 'text'
  if (HTML_TAG_RE.test(text)) return 'html'
  if (MARKDOWN_RE.test(text)) return 'markdown'
  return 'text'
}

function stripHtml(html) {
  if (typeof DOMParser === 'undefined') return html.replace(/<[^>]*>/g, ' ')
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body ? doc.body.textContent || '' : ''
}

function stripMarkdown(text) {
  return (
    text
      // 代码块 / 行内代码
      .replace(/```[\s\S]*?```/g, ' ')
      .replace(/`([^`]*)`/g, '$1')
      // 图片（丢弃）、链接（保留文字）
      .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      // 标题 / 引用 / 列表
      .replace(/^\s{0,3}#{1,6}\s+/gm, '')
      .replace(/^\s{0,3}>\s?/gm, '')
      .replace(/^\s{0,3}[-*+]\s+/gm, '')
      .replace(/^\s{0,3}\d+\.\s+/gm, '')
      // 表格：分隔行丢弃，内容行的竖线换成空格
      .replace(/^\s*\|[\s:|-]+\|\s*$/gm, ' ')
      .replace(/^\s*\|(.+)\|\s*$/gm, (match, inner) => inner.replace(/\|/g, ' '))
      // 强调：_ 可能出现在标识符中，只处理 * 与 __
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/__(.+?)__/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
  )
}

/**
 * 提取纯文本摘要（用于表格单行预览 / tooltip）
 * @param {*} content 原始内容
 * @returns {string}
 */
export function toPlainText(content) {
  const text = normalize(content)
  if (!text.trim()) return ''
  let plain = text
  if (HTML_TAG_RE.test(text)) {
    plain = stripHtml(text)
  } else if (MARKDOWN_RE.test(text)) {
    plain = stripMarkdown(text)
  }
  return plain.replace(/\s+/g, ' ').trim()
}

export default {
  name: 'MessageContent',
  props: {
    /** 消息内容，支持字符串（text/markdown/html）与非字符串（自动 JSON 美化） */
    content: {
      type: [String, Number, Object, Array],
      default: ''
    },
    /** block：完整渲染；inline：单行纯文本预览 */
    mode: {
      type: String,
      default: 'block'
    },
    /** auto：自动识别；也可强制指定 text / markdown / html */
    type: {
      type: String,
      default: 'auto'
    },
    /** 内容最大高度，超出滚动（数字按 px 处理） */
    maxHeight: {
      type: [String, Number],
      default: ''
    }
  },
  computed: {
    /** 字符串形式的 JSON 美化结果，非 JSON 为空串 */
    jsonText() {
      return typeof this.content === 'string' ? tryPrettyJson(this.content) : ''
    },
    normalized() {
      return this.jsonText || normalize(this.content)
    },
    inline() {
      return this.mode === 'inline'
    },
    contentType() {
      if (this.type !== 'auto') return this.type
      // JSON 美化后引号/括号很多，再交给识别函数可能误判成 markdown，这里固定按纯文本展示
      if (this.jsonText) return 'text'
      return detectContentType(this.normalized)
    },
    plainText() {
      return toPlainText(this.normalized)
    },
    rendered() {
      return this.contentType === 'markdown' ? md.render(this.normalized) : this.normalized
    },
    bodyStyle() {
      if (!this.maxHeight) return {}
      return {
        maxHeight: typeof this.maxHeight === 'number' ? `${this.maxHeight}px` : this.maxHeight,
        overflowY: 'auto'
      }
    }
  }
}
</script>

<style scoped>
.msg-content {
  min-width: 0;
}

.msg-inline {
  /* 用 -webkit-box + 行数限制代替 nowrap+ellipsis：
     antd 表格默认 auto 布局，nowrap 会让单元格 min-content 等于整段文本宽度，把表格撑破；
     换行 + break-all 后 min-content 降到单字符，任何容器里都不会溢出 */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  white-space: normal;
  word-break: break-all;
}

.msg-empty {
  color: rgba(0, 0, 0, 0.25);
}

.msg-text {
  margin: 0;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.85);
  white-space: pre-wrap;
  word-break: break-word;
}

.msg-body {
  font-size: 13px;
  line-height: 1.6;
  word-break: break-word;
}

/* v-html 注入的内容不带 scoped 标记，需用 ::v-deep 穿透 */
.msg-body ::v-deep img {
  max-width: 100%;
}

.msg-body ::v-deep pre {
  max-width: 100%;
  overflow-x: auto;
}

.msg-body ::v-deep table {
  display: table;
  width: auto;
  max-width: 100%;
}

/* html 内容（模板渲染结果）补齐基础排版 */
.msg-html ::v-deep p {
  margin: 0 0 8px;
}

.msg-html ::v-deep p:last-child {
  margin-bottom: 0;
}

.msg-html ::v-deep a {
  word-break: break-all;
}
</style>
